import { recordThrowEvent } from './throwCombo';

const PORTAL_BOARD_SPACING = 2.85;
const PORTAL_CAPTURE_RADIUS = 0.9;
const PORTAL_SECTOR_START = 6;
const PORTAL_SECTOR_END = 9;
const PORTAL_SPAWN_CHANCE = 0.4;

export const createEmptyPortalPair = () => ({
  entryIndex: null,
  exitIndex: null,
  used: false,
});

export const isPortalSector = (sector) =>
  Number.isInteger(sector) &&
  sector >= PORTAL_SECTOR_START &&
  sector <= PORTAL_SECTOR_END;

const validCell = (index) =>
  Number.isInteger(index) && index >= 0 && index < 25;

const generatePortalPair = (random = Math.random) => {
  const entryIndex = Math.floor(
    Math.max(0, Math.min(0.999999999999, random())) * 25
  );
  const exitOffset = Math.floor(
    Math.max(0, Math.min(0.999999999999, random())) * 24
  );
  return {
    entryIndex,
    exitIndex: exitOffset >= entryIndex ? exitOffset + 1 : exitOffset,
    used: false,
  };
};

const hash = (text) => {
  let value = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    value = Math.imul(value ^ text.charCodeAt(index), 16777619);
  }
  return value >>> 0;
};

const createRoomRandom = (seed) => {
  let draw = 0;
  return () => hash(`${seed}:${draw++}`) / 0x100000000;
};

export const generatePortalPairForRoom = (game, roomId) => {
  if (!isPortalSector(game?.floor)) return createEmptyPortalPair();
  const random = createRoomRandom(
    `${game.conditionSeed ?? 0}:${game.floor}:${roomId || ''}:portals`
  );
  if (random() >= PORTAL_SPAWN_CHANCE) return createEmptyPortalPair();
  return generatePortalPair(random);
};

export const getPortalPairState = (game) => {
  const pair = game.portalPair || {};
  const used = pair.used === true;
  if (Number.isInteger(game.floor) && !isPortalSector(game.floor)) {
    return {
      used,
      entryIndex: null,
      exitIndex: null,
      armed: false,
    };
  }
  const entryIndex = validCell(pair.entryIndex) ? pair.entryIndex : null;
  const exitIndex =
    validCell(pair.exitIndex) && pair.exitIndex !== entryIndex
      ? pair.exitIndex
      : null;
  return {
    used,
    entryIndex,
    exitIndex,
    armed: !used && entryIndex !== null && exitIndex !== null,
  };
};

export const activatePortalPair = (game) => {
  if (
    !getPortalPairState(game).armed ||
    !['rolled', 'bossPlayerRolled'].includes(game.phase) ||
    Number.isInteger(game.landingIndex)
  )
    return game;
  return {
    ...recordThrowEvent(game, 'portal'),
    portalPair: { ...game.portalPair, used: true },
  };
};

export const getPortalCellCenter = (index) => ({
  x: ((index % 5) - 2) * PORTAL_BOARD_SPACING,
  z: (Math.floor(index / 5) - 2) * PORTAL_BOARD_SPACING,
});

export const getPairedPortalIndex = (pair, index) => {
  if (
    !validCell(pair?.entryIndex) ||
    !validCell(pair?.exitIndex) ||
    pair.entryIndex === pair.exitIndex
  )
    return null;
  if (index === pair.entryIndex) return pair.exitIndex;
  if (index === pair.exitIndex) return pair.entryIndex;
  return null;
};

// Only translate position. Linear velocity, angular velocity, orientation,
// and height remain intact, so the normal bounce determines the landing.
// Both endpoints can be crossed; the die always emerges at the other one.
export const applyPortalTransit = (physics, position, pair, boardSurfaceY) => {
  if (
    !pair ||
    !validCell(pair.entryIndex) ||
    !validCell(pair.exitIndex) ||
    pair.entryIndex === pair.exitIndex ||
    physics.portalUsed ||
    physics.completed ||
    !physics.active ||
    physics.grounded ||
    position.y > boardSurfaceY + 0.9 ||
    position.y < boardSurfaceY
  )
    return false;
  const fromIndex = [pair.entryIndex, pair.exitIndex].find((index) => {
    const center = getPortalCellCenter(index);
    return (
      Math.hypot(position.x - center.x, position.z - center.z) <=
      PORTAL_CAPTURE_RADIUS
    );
  });
  const toIndex = getPairedPortalIndex(pair, fromIndex);
  if (toIndex === null) return false;
  const from = getPortalCellCenter(fromIndex);
  const to = getPortalCellCenter(toIndex);
  position.x += to.x - from.x;
  position.z += to.z - from.z;
  physics.portalUsed = true;
  physics.restingTime = 0;
  physics.rollDeadline = Math.max(
    physics.rollDeadline,
    physics.rollElapsed + 1
  );
  return { fromIndex, toIndex };
};
